<template>
  <b-modal
    v-model="modalShow"
    size="xl"
    scrollable
    title="Purchase Order Details"
    @hidden="$emit('modal-closed')"
  >

    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <b-alert
      variant="danger"
      :show="errorMessage ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ errorMessage }}
        </p>
      </div>
    </b-alert>

    <div v-if="!loading && !loadingError">
      <b-table-simple
        responsive
      >
        <b-tbody>
          <b-tr>
            <b-th>
              PO Number
            </b-th>
            <b-td>
              {{ poDetails.po_number }}
            </b-td>
            <b-th>
              Date
            </b-th>
            <b-td>
              {{ formatedDate(poDetails.date) }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>
              Company Gorup
            </b-th>
            <b-td>
              {{ poDetails.company_group__name }}
            </b-td>
            <b-th>
              Supplier
            </b-th>
            <b-td>
              {{ poDetails.supplier__identifier }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>
              PDF File
            </b-th>
            <b-td>
              <a
                v-if="poDetails.pdf_file"
                :href="poDetails.pdf_file"
                target="_blank"
              >
                <span>View File</span>
              </a>
            </b-td>
            <b-th>
              CSV File
            </b-th>
            <b-td style="width: 50%;">
              <a
                v-if="poDetails.csv_file"
                :href="poDetails.csv_file"
                target="_blank"
              >
                <span>View File</span>
              </a>
              <div
                v-if="isCSVuploadAllowed"
              >
                <po-csv-file-input
                  @change="onCsvFileChange"
                  @error="errorMessage = $event"
                />
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="(!csvFile && !fetchedFile) || savingCSV"
                  @click="submitCSVHandler"
                >
                  Save
                  <b-spinner
                    v-if="savingCSV"
                    small
                    label="Small Spinner"
                  />
                </b-button>
              </div>
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>
              Total Amount
            </b-th>
            <b-td>
              {{ formateNumber(poDetails.total_amount) }}
            </b-td>
            <b-th>
              Total Items
            </b-th>
            <b-td>
              {{ poDetails.total_items }} <span v-if="poDetails.matched_items"> / {{ poDetails.matched_items }}</span>
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>
              Status
            </b-th>
            <b-td>
              <status-icon
                v-b-tooltip.hover="{ boundary: 'window' }"
                :value="poDetails.status"
                :title="poDetails.status == 'Error' ? poDetails.error_message: ''"
              />
            </b-td>
            <b-th>
              Closed At
            </b-th>
            <b-td>
              <span v-if="poDetails.closed_at">
                {{ formatedDate(poDetails.closed_at, 'DD-MM-YYYY hh:mm A') }}
              </span>
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>
              Created By
            </b-th>
            <b-td>
              {{ poDetails.created_by__fullname }}
            </b-td>
            <b-th>
              Closed By
            </b-th>
            <b-td>
              {{ poDetails.closed_by__fullname }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>
              Remarks
            </b-th>
            <b-td colspan="3">
              {{ poDetails.remarks }}
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <h5>PO Items Match Details</h5>
    <p-o-items-match
      ref="poItemsMatch"
      :po-id="poId"
    />

    <template #modal-footer>
      <div class="w-100">
        <b-button
          variant="secondary"
          class="float-right"
          @click="modalShow=false"
        >
          Close
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BButton, BAlert, BSpinner, BTableSimple, BTbody, BTd, BTr, BTh, VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import StatusIcon from '../UI/StatusIcon.vue'
import POItemsMatch from '../PoInvoiceMatch/POItemsMatch.vue'
import PoCsvFileInput from './PoCsvFileInput.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BButton,
    BAlert,
    BSpinner,
    BTableSimple,
    BTbody,
    BTd,
    BTr,
    BTh,
    PoCsvFileInput,
    StatusIcon,
    POItemsMatch,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    poId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      poDetails: {},
      modalShow: true,
      loadingError: null,
      loading: true,
      csvFile: null,
      fetchedFile: null,
      savingCSV: false,
      errorMessage: null,
    }
  },
  computed: {
    isCSVuploadAllowed() {
      return !this.poDetails.csv_file && this.poDetails.status === 'Open'
    },
  },
  created() {
    this.fetchDetails()
  },
  methods: {
    fetchDetails() {
      axios.get(`/po/${this.poId}`)
        .then(res => {
          this.poDetails = res.data
          this.loadingError = null
          this.loading = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },
    onCsvFileChange({ csvFile, fetchedFile }) {
      this.csvFile = csvFile
      this.fetchedFile = fetchedFile
    },
    submitCSVHandler() {
      if (!this.isCSVuploadAllowed) {
        return
      }

      this.savingCSV = true

      const formData = new FormData()

      if (this.fetchedFile) {
        formData.append('csv_file_id', this.fetchedFile.file_id)
      } else if (this.csvFile) {
        formData.append('csv_file', this.csvFile)
      }

      axios.patch(`/po/${this.poId}/`, formData).then(res => {
        // Load updated details
        this.poDetails = res.data

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Csv file updated',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Refresh data in PO-Items natch component
        this.$refs.poItemsMatch.fetchDetails()
      }).catch(error => {
        this.loadingError = error?.response?.data?.error || 'Something went wrong'
      }).finally(() => {
        this.savingCSV = false
      })
    },
  },
}
</script>
